import { Component } from "projen";
import { NodeProject } from "projen/lib/javascript";
import { TextFile } from "projen/lib/textfile";
import {
  AstroConfigOptions,
  AstroIntegrationSpec,
} from "./astro-config-options";

/**
 * Renders `astro.config.mjs` for an Astro project.
 */
export class AstroConfig extends Component {
  /**
   * Find the AstroConfig component on a project.
   */
  public static of(project: NodeProject): AstroConfig | undefined {
    const isAstroConfig = (c: Component): c is AstroConfig =>
      c instanceof AstroConfig;
    return project.components.find(isAstroConfig);
  }

  private readonly configFilePath: string;
  private readonly site?: string;
  private readonly base?: string;
  private readonly output?: string;
  private readonly integrations: Array<AstroIntegrationSpec>;
  private readonly adapter?: AstroIntegrationSpec;

  constructor(
    public readonly project: NodeProject,
    options: AstroConfigOptions = {},
  ) {
    super(project);

    this.configFilePath = "astro.config.mjs";
    this.site = options.site;
    this.base = options.base;
    this.output = options.output;
    this.integrations = options.integrations ?? [];
    this.adapter = options.adapter;

    this.synthesizeConfig();
  }

  /**
   * Render the config file contents as an array of lines.
   *
   * Exposed for unit testing — `synthesizeConfig` writes these to disk.
   */
  public renderConfig(): Array<string> {
    const imports: Array<string> = [
      'import { defineConfig } from "astro/config";',
    ];
    const specs: Array<AstroIntegrationSpec> = [
      ...this.integrations,
      ...(this.adapter ? [this.adapter] : []),
    ];
    for (const spec of specs) {
      imports.push(renderImport(spec));
    }

    const body: Array<string> = [];
    if (this.site !== undefined) {
      body.push(`  site: ${JSON.stringify(this.site)},`);
    }
    if (this.base !== undefined) {
      body.push(`  base: ${JSON.stringify(this.base)},`);
    }
    if (this.output !== undefined) {
      body.push(`  output: ${JSON.stringify(this.output)},`);
    }
    if (this.integrations.length > 0) {
      const calls = this.integrations.map(renderCall).join(", ");
      body.push(`  integrations: [${calls}],`);
    }
    if (this.adapter) {
      body.push(`  adapter: ${renderCall(this.adapter)},`);
    }

    return [...imports, "", "export default defineConfig({", ...body, "});"];
  }

  private synthesizeConfig(): void {
    this.project.tryRemoveFile(this.configFilePath);
    new TextFile(this, this.configFilePath, {
      lines: this.renderConfig(),
    });
  }
}

function renderImport(spec: AstroIntegrationSpec): string {
  const asDefault = spec.defaultImport ?? true;
  const binding = asDefault ? spec.name : `{ ${spec.name} }`;
  return `import ${binding} from ${JSON.stringify(spec.importPath)};`;
}

function renderCall(spec: AstroIntegrationSpec): string {
  return `${spec.name}(${spec.args ?? ""})`;
}
